import type { IRetryCompanyDomainResult } from '@/api/organization/company-domain/retry-company-domain-mutation'
import type { ICompanyDomain } from '@/api/organization/company-domain/get-company-domain-query'

type ICompanyDomainUnavailableReason = ICompanyDomain['add']['unavailableReason']

/** Prevents a response started in one workspace from confirming UI state in another. */
export function isPreparedCompanyDomainOrganizationActive(
    activeOrganizationId: string | undefined,
    preparedOrganizationId: string
) {
    return activeOrganizationId === preparedOrganizationId
}

/** Explains why Clerk currently exposes no additional safely claimable domain. */
export function getCompanyDomainAddUnavailableMessage(
    reason: ICompanyDomainUnavailableReason,
    hasClaimedDomains: boolean
) {
    if (reason === 'domain_limit_reached') {
        return "This workspace has reached Clerk's limit of 10 claimed domains. Remove a domain before adding another."
    }
    if (reason === 'no_admins') {
        return 'This workspace has no administrators. Restore an administrator before adding a company domain.'
    }
    if (reason === 'no_customer_admins') {
        return 'No eligible customer domain was found. Superscale support accounts are excluded from customer-domain evidence.'
    }
    if (reason === 'admin_domains_disagree') {
        return 'The eligible administrator domains do not currently agree for this restricted test workspace.'
    }
    if (
        reason === 'admin_identity_missing' ||
        reason === 'admin_user_missing' ||
        reason === 'admin_primary_email_missing' ||
        reason === 'admin_primary_email_unverified'
    ) {
        return 'A non-Superscale administrator needs a verified work email before its domain can be added.'
    }
    if (reason === 'clerk_read_error' || reason === 'admin_inventory_changed') {
        return "We couldn't verify the workspace administrators in Clerk. Refresh the status and try again."
    }
    return hasClaimedDomains
        ? 'Every eligible administrator domain is already claimed by this workspace.'
        : 'No eligible company domains are available yet.'
}

/** Maps the backend's stable claim outcome to actionable, tenant-safe copy. */
export function getCompanyDomainFailureMessage(result: IRetryCompanyDomainResult) {
    if (result.reason === 'owned_by_another_organization') {
        return 'This company domain is already connected to another workspace.'
    }
    if (result.reason === 'clerk_common_domain' || result.reason === 'clerk_blocked_domain') {
        return "This email provider can't be claimed as a company domain."
    }
    if (
        result.reason === 'no_admins' ||
        result.reason === 'no_customer_admins' ||
        result.reason === 'admin_identity_missing' ||
        result.reason === 'admin_user_missing' ||
        result.reason === 'admin_primary_email_missing' ||
        result.reason === 'admin_primary_email_unverified' ||
        result.reason === 'admin_domains_disagree' ||
        result.reason === 'admin_inventory_changed' ||
        result.reason === 'clerk_read_error'
    ) {
        return getCompanyDomainAddUnavailableMessage(result.reason, false)
    }
    if (result.reason === 'claim_evidence_changed') {
        return 'Your permissions or the administrator email evidence changed. Refresh the status before retrying.'
    }
    if (result.reason === 'internal_domain') {
        return "Superscale domains can't be connected in this environment."
    }
    if (result.reason === 'domain_limit_reached') {
        return getCompanyDomainAddUnavailableMessage(result.reason, true)
    }
    return "We couldn't confirm the company domain. Refresh the status and try again."
}
